import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { Affix, Modal, Tabs, Tooltip } from "antd";

import { useGetAllCandidateAnswersHook } from "@/modules/assessments/hooks/use-get-candidate-answers.hook";
import { useBatchTestNormsHook } from "@/modules/assessments/scenes/AddTests/hooks/useBatchTestNorms.hook";
import { AssessmentService } from "@/modules/assessments/services/assessment.service";
import { IAssessmentTest, ILocalizedTestElement, LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import TableAnalysisScales from "@/modules/tables/TableAnalysisScales";

import { JobMatch } from "@/shared/components/assessment/report/JobMatch/JobMatch";
import { computeAssessmentScore } from "@/shared/components/assessment/service/computeAssessment.service";
import { TestAnalysisBlock } from "@/shared/components/assessment/report/TestAnalysisBlock/TestAnalysisBlock";
import {
  IAnsweredCompetency,
  IAnsweredScoreOverall,
  IAnsweredScoreScales,
} from "@/shared/components/charts/types/chartTypes";
import {
  computeAllTestScores,
  IComputeTestRequestData,
  ITestScoreResponse,
} from "@/shared/components/assessment/service/computeTest.service";
import { Loading } from "@/shared/components/Loading/Loading";

import { getLocaleDisplayName } from "@/shared/utils/locale.utils";
import { AnalysisContext, TTestAnalysisAnswers } from "@/shared/contexts/analysis.context";
import { AssessmentContext } from "@/shared/contexts/assessment.context";
import { IAssessmentInvite } from "@/shared/hooks/initializer/use-initialize-candidate.hook";

import { Button, ButtonColorType, ButtonSize } from "@/ui-components/commonComponents/Button/Button";

import { ReactComponent as DribbleSVG } from "@/assets/icons/feather-icons/dribbble.svg";

import style from "./style.module.css";
import { useGetAssessmentWithQuestionsAndAnswersHook } from "@/modules/candidate/hooks/useGetAssessmentWithQuestionsAndAnswers.hook";

const { TabPane } = Tabs;

export interface ITestAnswerScore {
  testId: number;
  competencies: IAnsweredCompetency;
  overall: IAnsweredScoreOverall;
  scales: IAnsweredScoreScales;
}

interface IAnalysisBlock {
  assessmentInvite?: IAssessmentInvite;
  container: HTMLElement | null;
}

export const getTestById = (tests: IAssessmentTest[] | undefined, id: number): IAssessmentTest | undefined => {
  return tests?.find((test) => test.testId === id);
};
const arrow = "\u2794";

export function AnalysisBlock({ assessmentInvite, container }: IAnalysisBlock) {
  const { assessment } = useContext(AssessmentContext);
  const { tests, testIds } = useMemo(() => {
    if (!assessment?.assessmentTests) return {};
    const tests = assessment.assessmentTests.sort((a, b) => a.rank - b.rank);
    return {
      tests,
      testIds: tests.map(({ testId }) => testId),
    };
  }, [assessment]);

  const { answers, isLoading: answersLoading } = useGetAllCandidateAnswersHook(
    Number(assessmentInvite?.inviteId),
    testIds,
  );
  // Admin report stays en-US even for a non-English candidate (audience rule), so force en-US
  // rather than the candidate's invite locale.
  const { questionsList, isLoading: questionsLoading } = useGetAssessmentWithQuestionsAndAnswersHook({
    inviteId: assessmentInvite?.inviteId,
    assessmentId: assessment?.assessmentId,
    locale: LanguagesIsoEnum.enUS,
  });
  const { normsMap, isLoading: normsLoading } = useBatchTestNormsHook(
    assessment?.clientId,
    assessment?.assessmentId,
    testIds || [],
  );

  const testElementsByTestId = useMemo(() => {
    if (!questionsList || questionsList.length === 0) {
      return {};
    }

    const flattened = questionsList.flat();
    const grouped: Record<number, ILocalizedTestElement[]> = {};

    flattened.forEach((element) => {
      if (!grouped[element.testId]) {
        grouped[element.testId] = [];
      }
      grouped[element.testId].push(element);
    });

    return grouped;
  }, [questionsList]);

  const [isLoading, setLoading] = useState<boolean>(false);

  const [initialTestAnswers, setInitialTestAnswers] = useState<TTestAnalysisAnswers[]>([]);
  const [updatedTestsAnswers, setUpdatedTestsAnswers] = useState<TTestAnalysisAnswers[]>([]);
  const [testResults, setTestResults] = useState<ITestScoreResponse[]>([]);

  const [selectedTestId, setSelectedTestId] = useState<number>();
  const [hasChangedElements, setHasChangedElements] = useState<boolean>(false);

  const [overallNetScore, setOverallNetScore] = useState<number | undefined>(assessmentInvite?.netScore);

  const [showModal, setShowModal] = useState<boolean>(false);

  const setInitialTestResults = useCallback(() => {
    const initialTestResults: ITestScoreResponse[] = answers.map(
      ({
        answer: {
          score: { competencies, scales, overall },
        },
        testId,
      }) => {
        let normId: number | undefined = undefined;
        if (tests) {
          const currentTest = tests.filter((test) => test.testId === testId)[0];
          if (currentTest) {
            normId = currentTest.normId;
          }
        }

        return {
          testId,
          normId,
          competenciesResult: competencies,
          overallScore: overall,
          scalesResult: scales,
        };
      },
    );
    setTestResults(initialTestResults);
  }, [answers, tests]);

  const getVariantName = useCallback(
    (testId: number): string => {
      const testAnswer = answers.find(({ testId: id }) => id === testId);
      const variantName = testAnswer?.answer.testVariant?.name;
      return variantName || "";
    },
    [answers],
  );

  const handleResetClick = () => {
    setUpdatedTestsAnswers(initialTestAnswers);
    setOverallNetScore(assessmentInvite?.netScore);
    setHasChangedElements(false);
    setInitialTestResults();
  };

  const handleRecalculateClick = async () => {
    const requestData: IComputeTestRequestData[] = updatedTestsAnswers.map(({ answers, testId, normId }) => {
      return {
        testId,
        payload: {
          answers,
          normId,
          config: getTestById(assessment?.assessmentTests, testId)?.config,
        },
      };
    });
    try {
      setLoading(true);

      const testScores: ITestScoreResponse[] | undefined = await computeAllTestScores(requestData);
      if (testScores) {
        setTestResults(testScores);
        const updatedTestScores: Omit<ITestAnswerScore, "testId">[] = testScores.map((score) => {
          return {
            scales: score.scalesResult,
            competencies: score.competenciesResult,
            overall: score.overallScore,
          };
        });
        const assessmentScore = await computeAssessmentScore({
          knockoutFormula: assessment?.knockoutFormula || "",
          testAnswers: updatedTestScores,
        });
        setOverallNetScore(assessmentScore.netScore);
      }
    } catch (e: any) {
      console.error(`Recalculation error: ${e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (assessment && answers.length > 0) {
      const castedAnswers: TTestAnalysisAnswers[] = answers.map(({ answer, testId }) => {
        return {
          testId,
          times: answer.times,
          normId: Number(getTestById(assessment.assessmentTests, testId)?.normId),
          answers: answer.answer.map((ans) => {
            return {
              elementId: ans.elementId,
              choiceKey: ans.answerKey,
            };
          }),
        };
      });

      setInitialTestAnswers(castedAnswers);
      setUpdatedTestsAnswers(castedAnswers);
      setInitialTestResults();
    }
  }, [assessment, answers, setInitialTestResults]);

  useEffect(() => {
    if (tests && tests.length !== 0) {
      setSelectedTestId(tests[0].testId);
    }
  }, [tests]);

  if (answersLoading || normsLoading || questionsLoading) {
    return <Loading />;
  }

  return (
    <AnalysisContext.Provider
      value={{
        initialTestAnswers,
        updatedTestsAnswers,
        testResults,
        normsMap,
        setUpdatedTestsAnswers,
        setHasChangedElements,
      }}
    >
      <Affix target={() => container}>
        <div className={style.analysisHeaderContainer}>
          <div className={style.analysisHeader}>
            {/* Only show JobMatch for assessments that do not use composites */}
            {typeof overallNetScore !== "undefined" &&
              typeof assessmentInvite?.netScore !== "undefined" &&
              overallNetScore !== null &&
              assessmentInvite.netScore !== null &&
              assessmentInvite.overallCompositeScore === null && (
                <JobMatch
                  matchStyle={AssessmentService.getOverallScoreLabel(overallNetScore)}
                  matchLabel={
                    AssessmentService.getOverallScoreLabel(overallNetScore) !==
                    AssessmentService.getOverallScoreLabel(assessmentInvite?.netScore)
                      ? `${AssessmentService.getOverallScoreLabel(
                          assessmentInvite?.netScore,
                        )} ${arrow} ${AssessmentService.getOverallScoreLabel(overallNetScore)}`
                      : AssessmentService.getOverallScoreLabel(assessmentInvite?.netScore)
                  }
                />
              )}
            {assessmentInvite?.locale && (
              <div className={style.languageBadge}>
                <span className={style.languageBadgeLabel}>Language:</span>
                {getLocaleDisplayName(assessmentInvite.locale)}
              </div>
            )}
            {hasChangedElements && (
              <div className={style.buttonsContainer}>
                <Button colorType={ButtonColorType.dark} buttonSize={ButtonSize.large} onClick={handleRecalculateClick}>
                  recalculate
                </Button>
                <Button
                  buttonSize={ButtonSize.small}
                  colorType={ButtonColorType.transparentDark}
                  onClick={handleResetClick}
                >
                  reset to default
                </Button>
              </div>
            )}
          </div>
          <Button colorType={ButtonColorType.dark} buttonSize={ButtonSize.large} onClick={() => setShowModal(true)}>
            scale details
          </Button>
        </div>
      </Affix>
      <div className={style.analysisContainer}>
        <Tabs activeKey={String(selectedTestId)} onChange={(key) => setSelectedTestId(Number(key))}>
          {tests &&
            tests.map((test) => {
              const variantName = getVariantName(test.testId);
              const currentTestElements = testElementsByTestId[test.testId];

              return (
                <TabPane
                  tab={
                    <span className={style.tabTitle}>
                      <p>
                        {test.test.name} (Question set: {variantName})
                      </p>
                      {test.test.includeInOverallScore && (
                        <Tooltip overlay={"Change answers and recalculate to see Overall Job Match difference"}>
                          <DribbleSVG />
                        </Tooltip>
                      )}
                    </span>
                  }
                  key={test.testId}
                >
                  <TestAnalysisBlock testId={test.testId} testElements={currentTestElements} />
                </TabPane>
              );
            })}
        </Tabs>
      </div>
      {isLoading && (
        <div className={style.loadingContainer}>
          <Loading />
        </div>
      )}

      <Modal
        title="Scale Details"
        className={style.scaleModal}
        visible={showModal}
        width="80%"
        onCancel={() => setShowModal(false)}
        onOk={() => setShowModal(false)}
        footer={[]}
      >
        <TableAnalysisScales tests={tests || []} loading={normsLoading} />
      </Modal>
    </AnalysisContext.Provider>
  );
}
